import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
  Provider,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChildrenOutletContexts,
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  RouteReuseStrategy,
} from '@angular/router';

import { filter } from 'rxjs';

import { IonRouterOutlet } from '@ionic/angular';

import {
  isRouteReuseKeyed,
  RouteReuseKeyedStrategy,
} from './route-reuse-strategy';

// Ionic's StackController saves a view's `url` once (createView) and finds
// (getExistingView) and pops (pop) views by it; a keyed reuse changes the
// address with no new view, so Back would return to the first address or
// setRoot the stack (#11677 A28). `url` is Ionic's own field: this is the one
// place that writes it, with Ionic's formula (@ionic/angular 8,
// ionic-angular-common.mjs `getUrl`). A view Ionic just made already holds it.
function ionicViewUrlsFollowKeyedRoutes(
  router: Router,
  outletContexts: ChildrenOutletContexts,
): void {
  for (
    let context = outletContexts.getContext(PRIMARY_OUTLET);
    context;
    context = context.children.getContext(PRIMARY_OUTLET)
  ) {
    const { outlet, route } = context;
    if (
      outlet instanceof IonRouterOutlet &&
      outlet.activatedView &&
      route &&
      isRouteReuseKeyed(route.routeConfig)
    ) {
      outlet.activatedView.url = router.serializeUrl(
        router.createUrlTree(['.'], { relativeTo: route }),
      );
    }
  }
}

/** The keyed reuse strategy, with Ionic's stack following its reuses. */
export function provideRouteReuseKeyed(): readonly (
  | Provider
  | EnvironmentProviders
)[] {
  return [
    { provide: RouteReuseStrategy, useClass: RouteReuseKeyedStrategy },
    provideAppInitializer(() => {
      const router = inject(Router);
      const outletContexts = inject(ChildrenOutletContexts);
      router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntilDestroyed(),
        )
        .subscribe(() =>
          ionicViewUrlsFollowKeyedRoutes(router, outletContexts),
        );
    }),
  ];
}
